import { PlanItem, Segment, CalibrationProfile } from '../../types'
import { calculateNutritionTotals, calculatePerHourRates } from '../../lib/nutrition'
import { Progress } from '../ui/progress'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table'
import { Badge } from '../ui/badge'

interface SharedNutritionViewProps {
  planItems: PlanItem[]
  segments: Segment[]
  calibrationProfile?: CalibrationProfile | null
}

export function SharedNutritionView({ planItems, segments, calibrationProfile }: SharedNutritionViewProps) {
  if (!planItems?.length) {
    return (
      <div className="bg-white rounded-2xl border border-neutral-200 shadow-minimal p-8">
        <h3 className="text-2xl font-bold text-primary-900 mb-4">Nutrition Plan</h3>
        <p className="text-neutral-600">No nutrition items planned for this run.</p>
      </div>
    )
  }

  const totals = calculateNutritionTotals(planItems)
  const perHourRates = calculatePerHourRates(segments, planItems)
  
  // Get targets from calibration profile or use defaults
  const targets = {
    carbs_g_per_h: { 
      low: calibrationProfile?.target_carbs_g_per_h_low || 60, 
      high: calibrationProfile?.target_carbs_g_per_h_high || 90 
    },
    fluids_ml_per_h: { 
      low: calibrationProfile?.target_fluids_ml_per_h_low || 500, 
      high: calibrationProfile?.target_fluids_ml_per_h_high || 750 
    },
    sodium_mg_per_h: { 
      low: calibrationProfile?.target_sodium_mg_per_h_low || 300, 
      high: calibrationProfile?.target_sodium_mg_per_h_high || 800 
    }
  }

  const getProgressColor = (value: number, target: { low: number; high: number }) => {
    if (value < target.low) return 'text-orange-600'
    if (value > target.high) return 'text-red-600'
    return 'text-green-600'
  }

  const getProgressValue = (value: number, target: { low: number; high: number }) => {
    const midpoint = (target.low + target.high) / 2
    return Math.min(100, (value / midpoint) * 100)
  }

  // Group plan items by type
  const snacks = planItems.filter(item => item.type === 'snack')
  const drinks = planItems.filter(item => item.type === 'drink')
  const notes = planItems.filter(item => item.type === 'note')

  return (
    <div className="space-y-8">
      {/* Nutrition Summary */}
      <div className="bg-white rounded-2xl border border-neutral-200 shadow-minimal">
        <div className="p-8">
          <h3 className="text-2xl font-bold text-primary-900 mb-6">Nutrition Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium">Carbohydrates</span>
                <span className={`text-sm font-bold ${getProgressColor(perHourRates.carbs_g, targets.carbs_g_per_h)}`}>
                  {perHourRates.carbs_g.toFixed(1)}g/hr
                </span>
              </div>
              <Progress 
                value={getProgressValue(perHourRates.carbs_g, targets.carbs_g_per_h)} 
                className="h-2 mb-1" 
              />
              <div className="text-xs text-neutral-600">
                Target: {targets.carbs_g_per_h.low}-{targets.carbs_g_per_h.high}g/hr
              </div>
              <div className="text-xs text-neutral-500">
                Total: {totals.carbs_g.toFixed(1)}g
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium">Fluids</span>
                <span className={`text-sm font-bold ${getProgressColor(perHourRates.fluids_ml, targets.fluids_ml_per_h)}`}>
                  {perHourRates.fluids_ml.toFixed(0)}ml/hr
                </span>
              </div>
              <Progress 
                value={getProgressValue(perHourRates.fluids_ml, targets.fluids_ml_per_h)} 
                className="h-2 mb-1" 
              />
              <div className="text-xs text-neutral-600">
                Target: {targets.fluids_ml_per_h.low}-{targets.fluids_ml_per_h.high}ml/hr
              </div>
              <div className="text-xs text-neutral-500">
                Total: {totals.fluids_ml.toFixed(0)}ml
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium">Sodium</span>
                <span className={`text-sm font-bold ${getProgressColor(perHourRates.sodium_mg, targets.sodium_mg_per_h)}`}>
                  {perHourRates.sodium_mg.toFixed(0)}mg/hr
                </span>
              </div>
              <Progress 
                value={getProgressValue(perHourRates.sodium_mg, targets.sodium_mg_per_h)} 
                className="h-2 mb-1" 
              />
              <div className="text-xs text-neutral-600">
                Target: {targets.sodium_mg_per_h.low}-{targets.sodium_mg_per_h.high}mg/hr
              </div>
              <div className="text-xs text-neutral-500">
                Total: {totals.sodium_mg.toFixed(0)}mg
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Snacks */}
      {snacks.length > 0 && (
        <div className="bg-white rounded-2xl border border-neutral-200 shadow-minimal">
          <div className="p-8">
            <h3 className="text-2xl font-bold text-primary-900 mb-6">Snacks & Food ({snacks.length} items)</h3>
          </div>
          <div className="border-t border-neutral-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead className="text-right">Carbs</TableHead>
                  <TableHead className="text-right">Sodium</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {snacks.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {item.segment_id ? 'During segment' : 'Aid station'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {item.carbs_g ? `${item.carbs_g}g` : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      {item.sodium_mg ? `${item.sodium_mg}mg` : '-'}
                    </TableCell>
                    <TableCell className="text-sm text-neutral-600">
                      {item.note || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Drinks */}
      {drinks.length > 0 && (
        <div className="bg-white rounded-2xl border border-neutral-200 shadow-minimal">
          <div className="p-8">
            <h3 className="text-2xl font-bold text-primary-900 mb-6">Drinks & Hydration ({drinks.length} items)</h3>
          </div>
          <div className="border-t border-neutral-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead className="text-right">Volume</TableHead>
                  <TableHead className="text-right">Carbs</TableHead>
                  <TableHead className="text-right">Sodium</TableHead>
                  <TableHead className="text-right">Caffeine</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {drinks.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {item.segment_id ? 'During segment' : 'Aid station'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {item.fluids_ml ? `${item.fluids_ml}ml` : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      {item.carbs_g ? `${item.carbs_g}g` : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      {item.sodium_mg ? `${item.sodium_mg}mg` : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      {item.caffeine_mg ? `${item.caffeine_mg}mg` : '-'}
                    </TableCell>
                    <TableCell className="text-sm text-neutral-600">
                      {item.note || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Notes */}
      {notes.length > 0 && (
        <div className="bg-white rounded-2xl border border-neutral-200 shadow-minimal">
          <div className="p-8">
            <h3 className="text-2xl font-bold text-primary-900 mb-6">Notes & Reminders ({notes.length} items)</h3>
            <div className="space-y-3">
              {notes.map((item) => (
                <div key={item.id} className="border border-neutral-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium">{item.name}</h4>
                    <Badge variant="outline">
                      {item.segment_id ? 'During segment' : 'Aid station'}
                    </Badge>
                  </div>
                  {item.note && (
                    <p className="text-sm text-neutral-600">{item.note}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}